import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type QueueEntry } from '../types';
import { formatVenueTime } from '../lib/time';
import { useLocationFilter } from '../lib/location-context';
import {
  loadLanguageOptions,
  optionsFor,
  type PlayLanguageOption,
} from '../lib/play-languages';
import { LanguageChipLabel } from './LanguageFlag';
import {
  markAsStarted,
  parkEntry,
  removeFromQueue,
  reorderQueue,
  setEntryPlayLanguage,
  setPlayLanguage,
  unparkEntry,
  type DequeueScope,
} from '../api/queue';

type Action = 'remove' | 'mark_started';

interface Props {
  entry: QueueEntry;
  /** 1-based count of queue entries, for clamping up/down arrows. */
  total: number;
  onError: (message: string) => void;
  onMutated: () => void;
}

export function QueueRow({ entry, total, onError, onMutated }: Props) {
  const [busy, setBusy] = useState(false);
  // null = no prompt; otherwise the action to execute on scope choice.
  const [pending, setPending] = useState<Action | null>(null);
  // Inline language picker, opened from the language badge. Its options are
  // the experience's own enabled languages (see lib/play-languages); the row
  // has room for the full list, so unlike the check-in modal it shows them all.
  const [langOpen, setLangOpen] = useState(false);
  const { locationId } = useLocationFilter();
  const [langOptions, setLangOptions] =
    useState<Record<string, PlayLanguageOption[]> | null>(null);

  // Lazy: only the row whose picker is actually opened pays for the lookup,
  // and the cache makes every row after the first free.
  useEffect(() => {
    if (!langOpen || !locationId) return;
    let cancelled = false;
    loadLanguageOptions(locationId)
      .then((opts) => {
        if (!cancelled) setLangOptions(opts);
      })
      .catch(() => {
        /* falls back to NL/EN */
      });
    return () => {
      cancelled = true;
    };
  }, [langOpen, locationId]);

  const lastName = entry.customer_last_name ?? '—';
  const partyPerson = `${lastName} ${entry.person_index}`;
  const hasSiblings = entry.party_size > 1;
  const parked = entry.parked === true;
  const langCode = entry.play_language ?? null;
  const langConfirmed = entry.play_language_confirmed === true;

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      onMutated();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function dispatch(action: Action, scope: DequeueScope) {
    setPending(null);
    const fn = action === 'remove' ? removeFromQueue : markAsStarted;
    await run(() => fn(entry.queue_entry_id, entry.departure_id, scope));
  }

  function onAction(action: Action) {
    if (hasSiblings) {
      // Multi-person party → ask the operator whether this applies to just
      // this individual or the whole departure.
      setPending(action);
    } else {
      void dispatch(action, 'one');
    }
  }

  // A queue row IS one person. Which setter applies depends on the mode the
  // departure is in: in per-person mode this row owns its own language, in
  // group mode the row shows the party's and changing it changes the party's.
  // The row never silently splits a group - splitting is done at check-in.
  const personal = entry.play_language_personal === true;

  async function pickLanguage(code: string) {
    setLangOpen(false);
    if (code === langCode && langConfirmed) return;
    await run(() =>
      personal
        ? setEntryPlayLanguage(entry.queue_entry_id, code).then(() => undefined)
        : setPlayLanguage(entry.departure_id, code),
    );
  }

  return (
    <div className={`queue-row ${parked ? 'queue-row-parked' : ''}`}>
      <div className="queue-row-position">{entry.position}</div>

      <div className="queue-row-time">
        {formatVenueTime(entry.departure_start)}
        {entry.room_name ? (
          <span className="queue-row-room muted">{entry.room_name}</span>
        ) : null}
      </div>

      <div className="queue-row-main">
        <Link
          to={`/booking/${entry.departure_number}`}
          className="queue-row-link"
        >
          <div className="queue-row-headline">
            <strong>{partyPerson}</strong>
            {hasSiblings ? (
              <>
                <span className="muted">·</span>
                <span>van {entry.party_size}</span>
              </>
            ) : null}
            <span className="muted">·</span>
            <span>{entry.experience_names ?? '—'}</span>
          </div>
          <div className="queue-row-sub muted">{entry.departure_number}</div>
        </Link>
        <div className="queue-row-meta">
          {langOpen ? (
            <span className="queue-row-lang-picker" role="radiogroup" aria-label="Speeltaal">
              <span className="queue-row-lang-scope muted">
                {personal ? `Persoon ${entry.person_index}` : 'Hele groep'}
              </span>
              {optionsFor(
                langOptions,
                entry.experiences?.[0]?.experience_id ?? null,
                langCode,
              ).map((l) => (
                <button
                  key={l.code}
                  type="button"
                  role="radio"
                  aria-checked={langCode === l.code}
                  aria-label={l.name}
                  title={l.name}
                  className={`walkin-quick-chip ${langCode === l.code ? 'walkin-quick-chip-active' : ''}`}
                  disabled={busy}
                  onClick={() => void pickLanguage(l.code)}
                >
                  <LanguageChipLabel code={l.code} />
                </button>
              ))}
              <button
                type="button"
                className="btn-link"
                onClick={() => setLangOpen(false)}
              >
                Annuleren
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={`queue-row-lang ${langConfirmed ? '' : 'queue-row-lang-unconfirmed'}`}
              disabled={busy}
              onClick={() => setLangOpen(true)}
              title={
                langConfirmed
                  ? personal
                    ? `Eigen speeltaal voor persoon ${entry.person_index}. Tik om te wijzigen.`
                    : 'Speeltaal van de hele groep (bevestigd). Tik om te wijzigen.'
                  : 'Speeltaal nog niet bevestigd (standaard uit de boekingstaal). Tik om te bevestigen.'
              }
              aria-label={`Speeltaal ${langCode ? langCode.toUpperCase() : 'onbekend'}${langConfirmed ? '' : ', nog niet bevestigd'}`}
            >
              {langCode ? <LanguageChipLabel code={langCode} /> : '?'}
              {personal ? <span className="queue-row-lang-personal">•</span> : null}
            </button>
          )}
          {parked ? (
            <span className="queue-row-parked-pill">Even weg</span>
          ) : entry.eta_minutes != null ? (
            <span className="queue-row-eta muted">~{entry.eta_minutes} min</span>
          ) : null}
        </div>
      </div>

      <div className="queue-row-reorder">
        <button
          type="button"
          className="btn-icon"
          aria-label="Een omhoog"
          disabled={busy || entry.position <= 1}
          onClick={() => void run(() => reorderQueue(entry.queue_entry_id, entry.position - 1))}
        >
          ▲
        </button>
        <button
          type="button"
          className="btn-icon"
          aria-label="Een omlaag"
          disabled={busy || entry.position >= total}
          onClick={() => void run(() => reorderQueue(entry.queue_entry_id, entry.position + 1))}
        >
          ▼
        </button>
      </div>

      <div className="queue-row-actions">
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() =>
            void run(() =>
              parked ? unparkEntry(entry.queue_entry_id) : parkEntry(entry.queue_entry_id),
            )
          }
          title={
            parked
              ? 'Klant is terug: weer aan de beurt op de eigen plek.'
              : 'Klant is even weg: houdt de plek, wordt overgeslagen tot Hervat.'
          }
        >
          {parked ? 'Hervat' : 'Parkeer'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() => onAction('mark_started')}
          title="Klant is al gestart — verwijder uit wachtrij zonder dat de operator het hoeft te doen."
        >
          Markeer gestart
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() => onAction('remove')}
          title="Haal uit de wachtrij: blijft aangekomen, maar staat niet meer in de rij (Parkeer houdt de plek)."
        >
          Verwijderen
        </button>
      </div>

      {pending ? (
        <div className="queue-row-scope-modal" role="dialog" aria-modal="true">
          <div className="queue-row-scope-modal-body">
            <div className="queue-row-scope-modal-title">
              {pending === 'remove' ? 'Verwijder uit wachtrij' : 'Markeer als gestart'}
            </div>
            <div className="queue-row-scope-modal-sub muted">
              Wil je dit alleen voor <strong>{partyPerson}</strong> doen,
              of voor het hele gezelschap ({entry.party_size} personen)?
            </div>
            <div className="queue-row-scope-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPending(null)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => void dispatch(pending, 'one')}
              >
                Alleen {partyPerson}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void dispatch(pending, 'party')}
              >
                Hele gezelschap
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
